import React from 'react'
import { useCoachChat } from './CoachChatContext'
import { useTranslation } from '~/hooks/useTranslation'
import CoachChatStatusBanner from './CoachChatStatusBanner'
import { getSelectClass, getSelectLabelClass } from './coachDashboardStyles'

const CoachChatSettings = ({
  currentAvailability,
  onAvailabilityChange,
  ringVolume: initialRingVolume = '0.8',
  ringToneOptions = [],
  messageSoundOptions = [],
  onRingVolumeChange,
  onRingToneChange,
  onMessageSoundChange,
  activeChats = [],
  embedded = false,
}) => {
  const { getDarkModeClasses } = useCoachChat()
  const { t } = useTranslation('coach_chat')
  // Initialize state from localStorage or defaults from options array
  const getInitialRingVolume = () => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('ring_volume')
        if (saved) {
          return saved
        }
      } catch (error) {
        console.warn('Error reading ring_volume from localStorage:', error)
      }
    }
    return initialRingVolume
  }

  const getInitialRingTone = () => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('ring_tone')
        // Validate saved value is in the options array
        if (saved && ringToneOptions.includes(saved)) {
          return saved
        }
      } catch (error) {
        console.warn('Error reading ring_tone from localStorage:', error)
      }
    }
    // Default to first option if available
    return ringToneOptions[0] || ''
  }

  const getInitialMessageSound = () => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('message_sound')
        // Validate saved value is in the options array
        if (saved && messageSoundOptions.includes(saved)) {
          return saved
        }
      } catch (error) {
        console.warn('Error reading message_sound from localStorage:', error)
      }
    }
    // Default to first option if available
    return messageSoundOptions[0] || ''
  }

  const [currentRingVolume, setCurrentRingVolume] =
    React.useState(getInitialRingVolume)
  const [currentRingTone, setCurrentRingTone] =
    React.useState(getInitialRingTone)
  const [currentMessageSound, setCurrentMessageSound] = React.useState(
    getInitialMessageSound,
  )

  // Refs for audio elements
  const ringToneTestRef = React.useRef(null)
  const messageNotificationRef = React.useRef(null)

  // Helper function to play ring tone test audio
  const playRingToneTest = (volume, sourceUrl) => {
    /* console.log(
      '[playRingToneTest] Called with volume:',
      volume,
      'sourceUrl:',
      sourceUrl,
    ) */
    const ringToneTest = ringToneTestRef.current
    // console.log('[playRingToneTest] Element found:', !!ringToneTest)
    if (ringToneTest) {
      // Update source if provided
      if (sourceUrl) {
        ringToneTest.src = sourceUrl
      }
      const volumeValue = parseFloat(volume || currentRingVolume)
      // console.log('[playRingToneTest] Setting volume to:', volumeValue)
      ringToneTest.volume = volumeValue
      /* console.log(
        '[playRingToneTest] Before load - readyState:',
        ringToneTest.readyState,
      ) */
      ringToneTest.load()
      /* console.log(
        '[playRingToneTest] After load - readyState:',
        ringToneTest.readyState,
      ) */
      const playAudio = () => {
        // console.log('[playRingToneTest] Attempting to play audio')
        /* console.log('[playRingToneTest] Audio state:', {
          readyState: ringToneTest.readyState,
          volume: ringToneTest.volume,
          src: ringToneTest.src,
          paused: ringToneTest.paused,
        }) */
        ringToneTest
          .play()
          .then(() => {
            // console.log('[playRingToneTest] Audio play() succeeded')
            return null
          })
          .catch((error) => {
            console.error(
              '[playRingToneTest] Could not play ring tone test:',
              error,
            )
          })
      }
      if (ringToneTest.readyState >= 3) {
        // Already loaded, play immediately
        /* console.log(
          '[playRingToneTest] Audio already loaded, playing immediately',
        ) */
        playAudio()
      } else {
        // Wait for canplaythrough event
        // console.log('[playRingToneTest] Waiting for canplaythrough event')
        ringToneTest.oncanplaythrough = () => {
          // console.log('[playRingToneTest] canplaythrough event fired')
          playAudio()
        }
      }
    } else {
      console.warn('[playRingToneTest] Audio element ref not available')
    }
  }

  // Helper function to play message notification sound at the shared sound volume
  const playMessageSound = (soundUrl, volume = currentRingVolume) => {
    const messageNotification = messageNotificationRef.current
    if (messageNotification) {
      // Use the same approach as the old chat.js - click the element to simulate user interaction
      // This works around browser autoplay policies
      messageNotification.src = soundUrl
      messageNotification.volume = parseFloat(volume)
      messageNotification.load()
      const playAudio = () => {
        // Click the element to simulate user interaction (required for autoplay)
        messageNotification.click()
        messageNotification.play().catch((error) => {
          console.warn('Could not play message sound:', error)
        })
      }
      if (messageNotification.readyState >= 3) {
        // Already loaded, play immediately
        playAudio()
      } else {
        // Wait for canplaythrough event
        messageNotification.oncanplaythrough = playAudio
      }
    }
  }

  // Sync state when options arrays change or validate current values
  React.useEffect(() => {
    // Validate and update ring tone if current value is not in options
    if (
      currentRingTone &&
      ringToneOptions.length > 0 &&
      !ringToneOptions.includes(currentRingTone)
    ) {
      setCurrentRingTone(ringToneOptions[0])
    } else if (!currentRingTone && ringToneOptions.length > 0) {
      setCurrentRingTone(ringToneOptions[0])
    }

    // Validate and update message sound if current value is not in options
    if (
      currentMessageSound &&
      messageSoundOptions.length > 0 &&
      !messageSoundOptions.includes(currentMessageSound)
    ) {
      setCurrentMessageSound(messageSoundOptions[0])
    } else if (!currentMessageSound && messageSoundOptions.length > 0) {
      setCurrentMessageSound(messageSoundOptions[0])
    }
  }, [
    ringToneOptions,
    messageSoundOptions,
    currentRingTone,
    currentMessageSound,
  ])

  // Initialize audio element sources when values change
  React.useEffect(() => {
    if (ringToneTestRef.current && currentRingTone) {
      ringToneTestRef.current.src = currentRingTone
    }
  }, [currentRingTone])

  React.useEffect(() => {
    if (messageNotificationRef.current && currentMessageSound) {
      messageNotificationRef.current.src = currentMessageSound
    }
  }, [currentMessageSound])

  // Notify parent of initial values from localStorage on mount
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const savedRingVolume = localStorage.getItem('ring_volume')
        const savedRingTone = localStorage.getItem('ring_tone')
        const savedMessageSound = localStorage.getItem('message_sound')

        // Notify parent of saved ring volume if present (state already initialized above)
        if (savedRingVolume && onRingVolumeChange) {
          onRingVolumeChange(savedRingVolume)
        }
        // Notify parent of saved ring tone if present (state already initialized above)
        if (
          savedRingTone &&
          ringToneOptions.includes(savedRingTone) &&
          onRingToneChange
        ) {
          onRingToneChange(savedRingTone)
        }
        // Notify parent of saved message sound if present (state already initialized above)
        if (
          savedMessageSound &&
          messageSoundOptions.includes(savedMessageSound) &&
          onMessageSoundChange
        ) {
          onMessageSoundChange(savedMessageSound)
        }
      } catch (error) {
        console.warn(
          'Error reading sound preferences from localStorage:',
          error,
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const containerClasses = embedded
    ? 'flex flex-shrink-0 flex-col gap-4'
    : `row-start-1 flex-shrink-0 rounded-lg border p-4 ${getDarkModeClasses(
        'border-gray-200 bg-white',
        'border-gray-600 bg-gray-800',
      )}`

  const selectClassName = embedded
    ? getSelectClass(getDarkModeClasses)
    : `w-full ${getDarkModeClasses(
        'bg-white text-gray-900',
        'rounded-sm bg-gray-700 px-2 py-1 text-white',
      )}`

  const labelClassName = embedded
    ? getSelectLabelClass(getDarkModeClasses)
    : `whitespace-nowrap ${getDarkModeClasses('text-gray-900', 'text-white')}`

  return (
    <div className={containerClasses}>
      {embedded && (
        <CoachChatStatusBanner currentAvailability={currentAvailability} />
      )}
      <div className="space-y-4">
        <div>
          {embedded && (
            <label
              htmlFor="current_state"
              className={getSelectLabelClass(getDarkModeClasses)}
            >
              {t('coach_settings.availability_label', 'Availability')}
            </label>
          )}
          <select
            id="current_state"
            className={selectClassName}
            value={currentAvailability}
            disabled={activeChats.length > 0}
            title={
              activeChats.length > 0
                ? 'Cannot change availability while in an active chat'
                : ''
            }
            onChange={(e) => {
              const value = e.target.value
              onAvailabilityChange(value)

              // Update URL parameter to keep URL in sync
              const url = new URL(window.location.href)
              url.searchParams.set('availability', value)
              window.history.replaceState({}, '', url.toString())

              // Dispatch custom event for Stimulus controller to listen to
              window.dispatchEvent(
                new CustomEvent('coachAvailabilityChanged', {
                  detail: { availability: value },
                }),
              )
            }}
          >
            <option value="away">
              {t(
                'coach_settings.availability_away',
                'Inactive – Not Available (Default)',
              )}
            </option>
            <option value="text_only">
              {t(
                'coach_settings.availability_text_only',
                'Live — Accepting Text Chats',
              )}
            </option>
            <option value="audio_video">
              {t(
                'coach_settings.availability_audio_video',
                'Live — Accepting Text/Audio/Video Chats',
              )}
            </option>
          </select>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="flex min-w-0 flex-col">
            <label htmlFor="ring_volume" className={labelClassName}>
              {t('coach_settings.ring_volume', 'Sound Volume:')}
            </label>
            <select
              id="ring_volume"
              className={selectClassName}
              value={currentRingVolume}
              onChange={(e) => {
                const value = e.target.value
                setCurrentRingVolume(value)
                if (onRingVolumeChange) {
                  onRingVolumeChange(value)
                }
                // Save to localStorage
                if (typeof window !== 'undefined') {
                  try {
                    localStorage.setItem('ring_volume', value)
                  } catch (error) {
                    console.warn(
                      'Error saving ring_volume to localStorage:',
                      error,
                    )
                  }
                }
                // Play preview with new volume
                playRingToneTest(value, currentRingTone)
              }}
            >
              <option value="0.1">10%</option>
              <option value="0.2">20%</option>
              <option value="0.3">30%</option>
              <option value="0.4">40%</option>
              <option value="0.5">50%</option>
              <option value="0.6">60%</option>
              <option value="0.7">70%</option>
              <option value="0.8">80%</option>
              <option value="0.9">90%</option>
              <option value="1">100%</option>
            </select>
          </div>

          <div className="flex min-w-0 flex-col">
            <label htmlFor="ring_tone" className={labelClassName}>
              {t('coach_settings.ring_tone', 'Ring Tone:')}
            </label>
            <select
              id="ring_tone"
              className={selectClassName}
              value={currentRingTone}
              disabled={ringToneOptions.length === 0}
              onChange={(e) => {
                const value = e.target.value
                setCurrentRingTone(value)
                if (onRingToneChange) {
                  onRingToneChange(value)
                }
                // Save to localStorage
                if (typeof window !== 'undefined') {
                  try {
                    localStorage.setItem('ring_tone', value)
                  } catch (error) {
                    console.warn(
                      'Error saving ring_tone to localStorage:',
                      error,
                    )
                  }
                }
                // Update and play preview
                playRingToneTest(currentRingVolume, value)
              }}
            >
              {ringToneOptions.map((url, index) => (
                <option key={index} value={url}>
                  Tone {index + 1}
                </option>
              ))}
            </select>
          </div>

          <div className="flex min-w-0 flex-col">
            <label htmlFor="message_sound" className={labelClassName}>
              {t('coach_settings.message_sound', 'Message Sound:')}
            </label>
            <select
              id="message_sound"
              className={selectClassName}
              value={currentMessageSound}
              disabled={messageSoundOptions.length === 0}
              onChange={(e) => {
                const value = e.target.value
                setCurrentMessageSound(value)
                if (onMessageSoundChange) {
                  onMessageSoundChange(value)
                }
                // Save to localStorage
                if (typeof window !== 'undefined') {
                  try {
                    localStorage.setItem('message_sound', value)
                  } catch (error) {
                    console.warn(
                      'Error saving message_sound to localStorage:',
                      error,
                    )
                  }
                }
                // Play preview
                playMessageSound(value)
              }}
            >
              {messageSoundOptions.map((url, index) => (
                <option key={index} value={url}>
                  Sound {index + 1}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/* Hidden audio elements for testing */}
      <audio ref={ringToneTestRef} preload="auto" />
      <audio ref={messageNotificationRef} preload="auto" />
    </div>
  )
}

export default CoachChatSettings
